import React from "react";
import { PageHeader } from "../components/PageHeader/PageHeader";

// ─── Types ────────────────────────────────────────────────────────────────────

type TicketStatus = "OPEN" | "IN_PROGRESS" | "COMPLETED" | "ON_HOLD" | "OVERDUE";
type TicketType = "breakdown" | "preventive" | "installation" | "inspection" | "request";

interface Ticket {
  id: string;
  number: string;
  title: string;
  type: TicketType;
  company: string;
  assetName: string;
  assetId: string;
  status: TicketStatus;
  assignees: { initials: string }[];
  notificationCount?: number;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_TICKETS: Ticket[] = [
  {
    id: "1", number: "TKT-10321", title: "Asset Repair & Maintenance",
    type: "request", company: "Acme International", assetName: "CNC Machine A", assetId: "M-2152",
    status: "OPEN", assignees: [{ initials: "JC" }, { initials: "JM" }, { initials: "DB" }, { initials: "TB" }], notificationCount: 2,
  },
  {
    id: "2", number: "TKT-10322", title: "Facility HVAC Maintenance Task",
    type: "preventive", company: "Beta Solutions", assetName: "HVAC Unit 3", assetId: "M-3041",
    status: "IN_PROGRESS", assignees: [{ initials: "MZ" }, { initials: "SP" }, { initials: "BP" }], notificationCount: 3,
  },
  {
    id: "3", number: "TKT-10323", title: "Emergency Breakdown Report",
    type: "breakdown", company: "Gamma Enterprises", assetName: "Lathe Machine B", assetId: "M-1987",
    status: "OPEN", assignees: [{ initials: "BP" }], notificationCount: 5,
  },
  {
    id: "4", number: "TKT-10324", title: "Press Machine Installation Request",
    type: "installation", company: "Delta Corp", assetName: "Press Machine 2", assetId: "M-2153",
    status: "IN_PROGRESS", assignees: [{ initials: "MZ" }, { initials: "SP" }], notificationCount: 1,
  },
  {
    id: "5", number: "TKT-10325", title: "Annual Safety Inspection",
    type: "inspection", company: "Epsilon Industries", assetName: "Generator Unit", assetId: "M-4010",
    status: "COMPLETED", assignees: [{ initials: "JC" }, { initials: "DB" }], notificationCount: 0,
  },
  {
    id: "6", number: "TKT-10326", title: "Hydraulic System Overhaul",
    type: "breakdown", company: "Zeta Manufacturing", assetName: "Forklift #5", assetId: "M-5001",
    status: "OVERDUE", assignees: [{ initials: "TB" }, { initials: "DB" }], notificationCount: 4,
  },
  {
    id: "7", number: "TKT-10327", title: "Preventive Maintenance Schedule",
    type: "preventive", company: "Acme Packaging Company", assetName: "Packmaster 2000", assetId: "PM-2000",
    status: "ON_HOLD", assignees: [{ initials: "MZ" }], notificationCount: 0,
  },
];

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<TicketStatus, { bg: string; color: string; label: string }> = {
  OPEN:        { bg: "#ccefeb", color: "#00b09b", label: "OPEN" },
  IN_PROGRESS: { bg: "#fff0e6", color: "#ff7a00", label: "IN PROGRESS" },
  COMPLETED:   { bg: "#e6f9f1", color: "#10b981", label: "COMPLETED" },
  ON_HOLD:     { bg: "#f3f4f6", color: "#748094", label: "ON HOLD" },
  OVERDUE:     { bg: "#fde5f0", color: "#ee0064", label: "OVERDUE" },
};


const TICKET_TYPE_CONFIG: Record<TicketType, { bg: string; color: string; icon: React.ReactNode }> = {
  request: {
    bg: "#e6e8fe", color: "#0517f8",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M13 2L6 13h7l-1 7 8-11h-8l1-7z" fill="#0517f8" />
      </svg>
    ),
  },
  preventive: {
    bg: "#ccefeb", color: "#00b09b",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#00b09b" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="7.5" />
        <path d="M11 7.5V11l2.5 2.5" />
      </svg>
    ),
  },
  breakdown: {
    bg: "#fde5f0", color: "#ee0064",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#ee0064" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 3.5l-1.5 3.5 3 1-4 5.5-1-3.5-3.5 1L11 4" />
        <path d="M7 15l-3 4M11 19h4" />
      </svg>
    ),
  },
  installation: {
    bg: "#fff0e6", color: "#ff7a00",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#ff7a00" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="3" />
        <path d="M11 2v2M11 18v2M2 11h2M18 11h2M4.93 4.93l1.41 1.41M15.66 15.66l1.41 1.41M4.93 17.07l1.41-1.41M15.66 6.34l1.41-1.41" />
      </svg>
    ),
  },
  inspection: {
    bg: "#f3e8ff", color: "#9333ea",
    icon: (
      <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="#9333ea" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="10" cy="10" r="6" />
        <path d="M14.5 14.5L19 19" />
      </svg>
    ),
  },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function DotsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="#748094">
      <circle cx="4" cy="10" r="1.5" />
      <circle cx="10" cy="10" r="1.5" />
      <circle cx="16" cy="10" r="1.5" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 4h12M4 8h8M6 12h4" />
    </svg>
  );
}

function ListViewIcon({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" strokeLinecap="round">
      <rect x="2" y="3" width="2" height="2" rx="0.5" fill={active ? "#0517f8" : "#748094"} />
      <path d="M6.5 4h7.5" stroke={active ? "#0517f8" : "#748094"} strokeWidth="1.4" />
      <rect x="2" y="7" width="2" height="2" rx="0.5" fill={active ? "#0517f8" : "#748094"} />
      <path d="M6.5 8h7.5" stroke={active ? "#0517f8" : "#748094"} strokeWidth="1.4" />
      <rect x="2" y="11" width="2" height="2" rx="0.5" fill={active ? "#0517f8" : "#748094"} />
      <path d="M6.5 12h7.5" stroke={active ? "#0517f8" : "#748094"} strokeWidth="1.4" />
    </svg>
  );
}

function TableViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#a2aab8" strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="12" height="12" rx="1.5" />
      <path d="M2 6h12M6 6v8" />
    </svg>
  );
}

function BoardViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#a2aab8" strokeWidth="1.4" strokeLinecap="round">
      <rect x="2" y="2" width="5" height="12" rx="1" />
      <rect x="9" y="2" width="5" height="7" rx="1" />
    </svg>
  );
}

function MapViewIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#a2aab8" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2L2 4v10l4-2 4 2 4-2V2l-4 2-4-2z" />
      <path d="M6 2v10M10 4v10" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 2H2a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V6" />
      <path d="M6 1h3v3M9 1L5 5" />
    </svg>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TicketStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "4px 6px", borderRadius: 4,
      fontSize: 12, fontWeight: 700, lineHeight: "16px", letterSpacing: "-0.24px",
      background: cfg.bg, color: cfg.color, fontFamily: "var(--font-family-base)", whiteSpace: "nowrap",
    }}>
      {cfg.label}
    </span>
  );
}

function AvatarGroup({ assignees }: { assignees: { initials: string }[] }) {
  if (!assignees.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      {assignees.slice(0, 4).map((a, i) => (
        <div key={i} style={{
          width: 24, height: 24, borderRadius: "50%",
          background: "#e6e8fe", border: "2px solid white",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 700, color: "#0517f8",
          fontFamily: "var(--font-family-base)", letterSpacing: "-0.2px",
          marginLeft: i > 0 ? -4 : 0, flexShrink: 0,
          position: "relative", zIndex: 4 - i,
        }}>
          {a.initials}
        </div>
      ))}
    </div>
  );
}

// ─── Ticket Card ──────────────────────────────────────────────────────────────

interface TicketCardProps {
  ticket: Ticket;
  hovered: boolean;
  onHover: (id: string | null) => void;
  onClick: () => void;
}

function TicketCard({ ticket, hovered, onHover, onClick }: TicketCardProps) {
  const typeConfig = TICKET_TYPE_CONFIG[ticket.type];
  return (
    <div
      style={{
        background: hovered ? "#f9fafb" : "white",
        border: "1px solid var(--color-border-primary)",
        borderRadius: 8, padding: 16,
        display: "flex", alignItems: "center", gap: 16,
        cursor: "pointer", transition: "background 80ms ease",
      }}
      onMouseEnter={() => onHover(ticket.id)}
      onMouseLeave={() => onHover(null)}
      onClick={onClick}
    >
      {/* Type icon with badges */}
      <div style={{ position: "relative", flexShrink: 0 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 10,
          background: typeConfig.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {typeConfig.icon}
        </div>

        {/* Notification badge — bottom-left */}
        {!!ticket.notificationCount && (
          <div style={{
            position: "absolute", bottom: -4, left: -4,
            background: "#ee0064", border: "2px solid white",
            borderRadius: 99, padding: "2px 5px",
            fontSize: 10, fontWeight: 700, color: "white",
            fontFamily: "var(--font-family-base)", lineHeight: "14px",
            minWidth: 16, textAlign: "center",
          }}>
            {ticket.notificationCount}
          </div>
        )}

        {/* External link badge — top-right */}
        <div style={{
          position: "absolute", top: -4, right: -4,
          width: 16, height: 16, borderRadius: 4,
          background: "#a2aab8",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ExternalLinkIcon />
        </div>
      </div>

      {/* Title & meta */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        <p style={{
          fontSize: 16, fontWeight: 700, lineHeight: "24px", letterSpacing: "-0.32px",
          color: "var(--color-content-primary)", fontFamily: "var(--font-family-base)",
          margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {ticket.title}
        </p>
        <div style={{
          display: "flex", alignItems: "center", gap: 4,
          fontSize: 12, fontWeight: 500, color: "#748094",
          fontFamily: "var(--font-family-base)", lineHeight: "16px",
        }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            #{ticket.number}
          </span>
          <span>•</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ticket.company}
          </span>
          <span>•</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ticket.assetName}
          </span>
          <span>•</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ticket.assetId}
          </span>
        </div>
      </div>

      {/* Right: avatars + status + more */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <AvatarGroup assignees={ticket.assignees} />
        <StatusBadge status={ticket.status} />
        <button
          style={{
            border: "none", background: "transparent", cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            padding: 0, borderRadius: 4, flexShrink: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DotsIcon />
        </button>
      </div>
    </div>
  );
}

// ─── View Pill ────────────────────────────────────────────────────────────────

type ViewMode = "list" | "table" | "board" | "map";

const VIEW_OPTIONS: { key: ViewMode; label: string; Icon: React.FC<{ active: boolean }> }[] = [
  { key: "list",  label: "List",  Icon: ListViewIcon },
  { key: "table", label: "Table", Icon: ({ active }: { active: boolean }) => <TableViewIcon /> },
  { key: "board", label: "Board", Icon: ({ active }: { active: boolean }) => <BoardViewIcon /> },
  { key: "map",   label: "Map",   Icon: ({ active }: { active: boolean }) => <MapViewIcon /> },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

interface TicketsListPageProps {
  onOpenTicket: (ticketId: string) => void;
}

export function TicketsListPage({ onOpenTicket }: TicketsListPageProps) {
  const [viewMode,    setViewMode]    = React.useState<ViewMode>("list");
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", background: "white" }}>
      <PageHeader
        breadcrumbs={[{ label: "Tickets" }]}
        searchAction={() => {}}
        primaryAction={{ label: "New Ticket", onClick: () => {} }}
      />

      {/* View & filter bar */}
      <div style={{
        height: 60, flexShrink: 0,
        background: "white",
        borderBottom: "1px solid var(--color-border-primary)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 24px",
      }}>
        {/* Pill view tabs */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {VIEW_OPTIONS.map(({ key, label, Icon }) => {
            const active = viewMode === key;
            return (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "6px 8px", borderRadius: 4,
                  border: "none", cursor: "pointer",
                  background: active ? "#e6e8fe" : "transparent",
                  fontSize: 12, fontWeight: 700, letterSpacing: "-0.24px",
                  color: active ? "var(--color-content-primary)" : "#a2aab8",
                  fontFamily: "var(--font-family-base)", transition: "all 100ms",
                }}
              >
                <Icon active={active} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Filters button */}
        <button style={{
          height: 34, padding: "0 12px",
          border: "1px solid var(--color-border-primary)",
          borderRadius: 8, background: "white", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 6,
          fontFamily: "var(--font-family-base)", fontSize: 12, fontWeight: 700,
          color: "var(--color-content-primary)",
        }}>
          <FilterIcon />
          Filters
        </button>
      </div>

      {/* Ticket list */}
      <div style={{ flex: 1, overflowY: "auto", background: "white", padding: "16px 24px 24px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {MOCK_TICKETS.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              hovered={hoveredCard === ticket.id}
              onHover={setHoveredCard}
              onClick={() => onOpenTicket(ticket.id)}
            />
          ))}
          {MOCK_TICKETS.length === 0 && (
            <div style={{
              textAlign: "center", padding: "64px 24px",
              color: "#a2aab8", fontSize: 14, fontWeight: 500,
              fontFamily: "var(--font-family-base)",
            }}>
              No tickets found
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
